/**
 * Left Progress Sidebar
 * Shows progress through Bridge Session flow with status indicators.
 * Displays domains, entropy heat map, bridge card, and control bridge.
 * @feature BRIDGE_SESSION_FLOW - Session step progress and navigation
 */

import React from 'react';
import { List, Typography, Space } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  LockOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { useBridgeSession } from '../../contexts/BridgeSessionContext';
import { isLocalBridgeApiEnabled } from '../../services/bridgeSessionDataAdapter';
import { BridgeSessionStep, DomainState } from '../../types/bridgeSession';
import { isBridgeSessionStepAtOrBefore } from '../../utils/bridgeSessionResumeRoutes';
import styles from './LeftProgressSidebar.module.css';

const { Text } = Typography;

interface SidebarItem {
  id: string;
  label: string;
  step?: BridgeSessionStep;
  domainId?: string;
  subdomainId?: string;
  icon?: React.ReactNode;
  children?: SidebarItem[];
}

/** True when session state indicates a later step is reachable (interrupted/resumed sessions). */
function isSessionProgressUnlocked(
  sessionState: import('../../types/bridgeSession').BridgeSessionState
): boolean {
  const bridgeCard = sessionState.bridgeCard.state;
  return (
    sessionState.entropyHeatMap.state === 'available' ||
    bridgeCard === 'complete' ||
    bridgeCard === 'active' ||
    sessionState.controlBridge.state === 'available'
  );
}

const getStatusIcon = (state: DomainState | 'available' | 'locked' | 'incomplete' | 'complete' | 'active' | 'expired' | 'reviewed') => {
  switch (state) {
    case 'sufficient':
    case 'complete':
    case 'available':
      return <CheckCircleOutlined className={styles.statusIconComplete} />;
    case 'in_progress':
    case 'incomplete':
      return <ClockCircleOutlined className={styles.statusIconInProgress} />;
    case 'active':
      return <AppstoreOutlined className={styles.statusIconActive} />;
    case 'locked':
    case 'expired':
      return <LockOutlined className={styles.statusIconLocked} />;
    default:
      return null;
  }
};

const getStatusText = (state: DomainState | 'available' | 'locked' | 'incomplete' | 'complete' | 'active' | 'expired' | 'reviewed') => {
  switch (state) {
    case 'sufficient':
    case 'complete':
      return 'Complete';
    case 'in_progress':
    case 'incomplete':
      return 'In Progress';
    case 'active':
      return 'Active Bridge';
    case 'locked':
    case 'expired':
    case 'reviewed':
      return 'Locked';
    case 'available':
      return 'Available';
    default:
      return '';
  }
};

export const LeftProgressSidebar: React.FC = () => {
  const {
    sessionState,
    currentStep,
    navigateToStep,
    navigateToDomain,
    selectedDomainId,
    getDomainState,
  } = useBridgeSession();

  // Build sidebar items from session state (hierarchical domains/subdomains)
  // Must be called before early return to follow React Hooks rules
  const sidebarItems: SidebarItem[] = React.useMemo(() => {
    const items: SidebarItem[] = [];
    const showQuestionsStep = isLocalBridgeApiEnabled();

    if (showQuestionsStep) {
      items.push({
        id: 'questions',
        label: 'Questions',
        step: 'questions',
      });
    }

    if (sessionState) {
      // Add domain items with subdomains
      Object.entries(sessionState.domains).forEach(([domainId, domain]) => {
        const domainItem: SidebarItem = {
          id: domainId,
          label: domain.name,
          step: 'domain_builder',
          domainId: domainId,
          children: domain.subdomains.map(subdomain => ({
            id: subdomain.artifact_item_id,
            label: subdomain.name,
            step: 'domain_builder',
            domainId: domainId,
            subdomainId: subdomain.artifact_item_id,
          })),
        };
        items.push(domainItem);
      });
    } else {
      // Fallback to default structure if no session state
      items.push({
        id: 'business-domain',
        label: 'Business Domain',
        step: 'domain_builder',
        children: [
          { id: 'strategy', label: 'Strategy', step: 'domain_builder', domainId: 'strategy' },
          { id: 'resources', label: 'Resources', step: 'domain_builder', domainId: 'resources' },
          { id: 'finance', label: 'Finance', step: 'domain_builder', domainId: 'finance' },
          { id: 'product', label: 'Product', step: 'domain_builder', domainId: 'product' },
          { id: 'revenue', label: 'Revenue', step: 'domain_builder', domainId: 'revenue' },
          { id: 'operations', label: 'Operations', step: 'domain_builder', domainId: 'operations' },
        ],
      });
    }

    // Add entropy heat map, friction priorities (local API), define conditions, bridge card, and control bridge
    items.push(
      {
        id: 'entropy-heat-map',
        label: 'Entropy Heat Map',
        step: 'entropy_heat_map',
      },
      ...(showQuestionsStep
        ? [
            {
              id: 'friction-priorities',
              label: 'Friction Priorities',
              step: 'friction_priorities' as BridgeSessionStep,
            },
            {
              id: 'define-conditions',
              label: 'Define Conditions',
              step: 'define_conditions' as BridgeSessionStep,
            },
          ]
        : []),
      {
        id: 'bridge-card',
        label: 'Bridge Card',
        step: 'bridge_card',
      },
      {
        id: 'control-bridge',
        label: 'Control Bridge',
        step: 'control_bridge',
      }
    );

    return items;
  }, [sessionState]);

  const handleItemClick = (item: SidebarItem, canNavigate: boolean, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!canNavigate) return;
    if (item.domainId) {
      navigateToDomain(item.domainId, item.subdomainId);
      return;
    }
    if (item.step) {
      navigateToStep(item.step);
    }
  };

  if (!sessionState) {
    return (
      <div className={styles.sidebar}>
        <div className={styles.emptyState}>
          <Text type="secondary">No active Bridge Session</Text>
        </div>
      </div>
    );
  }

  const renderItem = (item: SidebarItem, level: number = 0) => {
    const domainState = item.domainId ? getDomainState(item.domainId) : null;
    const sessionUnlocked = isSessionProgressUnlocked(sessionState);
    // Questions: prior/current always reachable for facilitators (was permanently locked when not current).
    const state = item.id === 'questions'
      ? (currentStep === 'questions' ? 'in_progress' : 'available')
      : item.id === 'friction-priorities'
      ? (currentStep === 'friction_priorities'
          ? 'in_progress'
          : isBridgeSessionStepAtOrBefore('friction_priorities', currentStep) ||
              sessionState.entropyHeatMap.state === 'available'
            ? 'available'
            : 'locked')
      : item.id === 'define-conditions'
      ? (currentStep === 'define_conditions'
          ? 'in_progress'
          : isBridgeSessionStepAtOrBefore('define_conditions', currentStep) ||
              sessionUnlocked
            ? 'available'
            : 'locked')
      : item.domainId
      ? domainState?.state || 'locked'
      : item.id === 'entropy-heat-map'
      ? (currentStep === 'entropy_heat_map'
          ? 'in_progress'
          : isBridgeSessionStepAtOrBefore('entropy_heat_map', currentStep)
            ? 'available'
            : sessionState.entropyHeatMap.state)
      : item.id === 'bridge-card'
      ? (currentStep === 'bridge_card'
          ? 'in_progress'
          : isBridgeSessionStepAtOrBefore('bridge_card', currentStep)
            ? 'available'
            : sessionState.bridgeCard.state)
      : item.id === 'control-bridge'
      ? (currentStep === 'control_bridge'
          ? 'in_progress'
          : isBridgeSessionStepAtOrBefore('control_bridge', currentStep)
            ? 'available'
            : sessionState.controlBridge.state)
      : 'locked';

    const isCurrent =
      item.domainId && currentStep === 'domain_builder'
        ? selectedDomainId === item.domainId
        : currentStep === item.step;
    const stepReachable =
      !!item.step &&
      (isCurrent ||
        isBridgeSessionStepAtOrBefore(item.step, currentStep) ||
        state === 'sufficient' ||
        state === 'complete' ||
        state === 'available' ||
        state === 'active' ||
        state === 'in_progress');
    const isClickable = stepReachable;
    const statusIcon = getStatusIcon(state);
    const statusText = getStatusText(state);

    const content = (
      <Space>
        {statusIcon}
        <Text className={styles.itemLabel}>{item.label}</Text>
        {statusText && (
          <Text type="secondary" className={styles.statusText}>
            {statusText}
          </Text>
        )}
      </Space>
    );
    const itemClassName = `${styles.sidebarItem} ${level > 0 ? styles.nestedItem : ''} ${isCurrent ? styles.currentItem : ''} ${isClickable ? styles.clickable : styles.disabled}`;
    const itemStyle = { paddingLeft: `${16 + level * 24}px` };

    // Use List.Item only at top level to avoid <li> inside <li>; nest with div for children
    if (level > 0) {
      return (
        <div
          key={item.id}
          className={itemClassName}
          style={itemStyle}
          onClick={(e) => isClickable && handleItemClick(item, isClickable, e)}
          role="button"
          tabIndex={isClickable ? 0 : -1}
          onKeyDown={(e) => isClickable && (e.key === 'Enter' || e.key === ' ') && handleItemClick(item, isClickable)}
        >
          {content}
        </div>
      );
    }
    return (
      <List.Item
        key={item.id}
        className={itemClassName}
        style={itemStyle}
        onClick={(e) => isClickable && handleItemClick(item, isClickable, e)}
      >
        {content}
        {item.children && item.children.length > 0 && (
          <div className={styles.children}>
            {item.children.map(child => renderItem(child, level + 1))}
          </div>
        )}
      </List.Item>
    );
  };

  return (
    <div className={styles.sidebar}>
      <div className={styles.header}>
        <Text strong>Bridge Session</Text>
      </div>
      <List
        className={styles.list}
        dataSource={sidebarItems}
        renderItem={(item) => renderItem(item)}
      />
    </div>
  );
};
